import React, { useState, useEffect } from 'react';
import { EdgeWhitespaceNotice, fieldsWithEdgeWhitespace } from '../utils/accountFieldHygiene';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import GoogleSignInButton from './shared/GoogleSignInButton';
import { parseGoogleCredentialEmail } from '../utils/googleGsi';
import {
  detectSchoolSubdomainFromLocation,
  getPublicAuthOrigin,
  isDistrictSubdomainHost,
  isOAuthAuthHost,
  isProductionMultiHostAuth
} from '../utils/authOrigin';
import { finishAuthAndRedirect } from '../utils/postAuthRedirect';
import AuthPageShell from './AuthPageShell';
import './Login.css';

const RegisterParent = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    registration_code: ''
  });
  
  const [subdomain, setSubdomain] = useState('');
  const [districtName, setDistrictName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeValidated, setCodeValidated] = useState(false);
  const [googleCredential, setGoogleCredential] = useState(null);
  const [emailFromGoogle, setEmailFromGoogle] = useState(false);

  useEffect(() => {
    const detectedSubdomain = detectSchoolSubdomainFromLocation(searchParams);
    setSubdomain(detectedSubdomain);

    if (
      isProductionMultiHostAuth() &&
      !isOAuthAuthHost() &&
      isDistrictSubdomainHost() &&
      detectedSubdomain
    ) {
      sessionStorage.setItem('auth_return_to', `${window.location.origin}/parent`);
      const u = new URL(`${getPublicAuthOrigin()}/register`);
      u.searchParams.set('subdomain', detectedSubdomain);
      window.location.replace(u.toString());
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleValidateCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.registration_code || !subdomain) {
      setError('Please enter a registration code');
      return;
    }

    try {
      const response = await axios.post('/api/registration-codes/validate', {
        code: formData.registration_code,
        subdomain: subdomain
      });

      if (response.data.valid) {
        setCodeValidated(true);
        setDistrictName(response.data.district_name);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Invalid registration code');
    }
  };

  const handleGoogleForRegister = (credential) => {
    setError('');
    const email = parseGoogleCredentialEmail(credential);
    if (!email) {
      setError('Could not read email from Google. Please try again.');
      return;
    }
    setGoogleCredential(credential);
    setEmailFromGoogle(true);
    setFormData((prev) => ({ ...prev, email }));
  };

  const clearGoogleAndUsePassword = () => {
    setGoogleCredential(null);
    setEmailFromGoogle(false);
    setFormData((prev) => ({ ...prev, email: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    /**
     * Edge whitespace is refused rather than silently trimmed. The server trims anyway, so this
     * is not about what gets stored - it is about the person seeing the username they will have
     * to type at sign-in. A silent trim stores the right value and leaves them believing the
     * space was part of it.
     */
    const dirty = fieldsWithEdgeWhitespace({ Username: formData.username, Email: formData.email });
    if (dirty.length > 0) {
      setError(`Remove the extra space at the start or end of your ${dirty.join(' and ').toLowerCase()} before continuing.`);
      return;
    }

    if (googleCredential) {
      if (!formData.username.trim() || !formData.email.trim()) {
        setError('Username and email are required');
        return;
      }
      setLoading(true);
      try {
        const response = await axios.post('/api/auth/register-parent-google', {
          credential: googleCredential,
          username: formData.username,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          registration_code: formData.registration_code,
          subdomain
        });
        const payload = response.data?.data || response.data;
        const token = payload?.token;
        const user = payload?.user;
        if (!token || !user?.role) {
          setError('Invalid server response. Please try again.');
          return;
        }
        setAuth(token, user);
        await finishAuthAndRedirect(navigate, user);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to create account');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/api/auth/register-parent', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        registration_code: formData.registration_code,
        subdomain: subdomain
      });

      const payload = response.data?.data || response.data;
      const token = payload?.token;
      const user = payload?.user;

      if (!token || !user?.role) {
        setError('Invalid server response. Please try again.');
        return;
      }

      setAuth(token, user);
      await finishAuthAndRedirect(navigate, user);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  if (!subdomain) {
    return (
      <AuthPageShell>
        <div className="login-box">
          <h1>Back to School Forms</h1>
          <h2>Registration</h2>
          <div className="error-message">
            Unable to detect district. Please visit your district's registration page.
          </div>
        </div>
      </AuthPageShell>
    );
  }

  if (!codeValidated) {
    return (
      <AuthPageShell>
        <div className="login-box">
          <h1>Back to School Forms</h1>
          <h2>Parent Registration</h2>
          <p style={{ marginBottom: '1.5rem', color: '#666', textAlign: 'center' }}>
            Enter your registration code to continue
          </p>
          {error && <div className="error-message">{error}</div>}
          <form onSubmit={handleValidateCode}>
            <div className="form-group">
              <label>Registration Code *</label>
              <input
                type="text"
                name="registration_code"
                value={formData.registration_code}
                onChange={handleChange}
                required
                placeholder="Enter your registration code"
                autoFocus
              />
            </div>
            <button type="submit" className="btn-primary">Continue</button>
          </form>
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <Link to="/login" style={{ color: '#667eea', textDecoration: 'none', fontSize: '0.9rem' }}>
              Already have an account? Login
            </Link>
          </div>
        </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <div className="login-box" style={{ maxWidth: '500px' }}>
        <h1>Back to School Forms</h1>
        <h2>Create Parent Account</h2>
        <p style={{ marginBottom: '1.5rem', color: '#666', textAlign: 'center' }}>
          Registering for: <strong>{districtName}</strong>
        </p>
        {error && <div className="error-message">{error}</div>}

        {!googleCredential ? (
          <>
            <p style={{ textAlign: 'center', color: '#666', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
              Recommended: sign in with Google to create your account.
            </p>
            <GoogleSignInButton text="signup_with" onCredential={handleGoogleForRegister} />
            <p
              style={{
                textAlign: 'center',
                color: '#666',
                fontSize: '0.85rem',
                margin: '1rem 0'
              }}
            >
              or register with a password below
            </p>
          </>
        ) : (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              background: '#f0f7ff',
              borderRadius: 8,
              fontSize: '0.9rem',
              color: '#333'
            }}
          >
            <strong>Google</strong> will be used for this account ({formData.email}).
            <button
              type="button"
              onClick={clearGoogleAndUsePassword}
              style={{
                marginLeft: '0.5rem',
                background: 'none',
                border: 'none',
                color: '#3498db',
                cursor: 'pointer',
                textDecoration: 'underline',
                font: 'inherit'
              }}
            >
              Use password instead
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </div>
          </div>

          <EdgeWhitespaceNotice
            fields={{ Username: formData.username, Email: formData.email }}
            onFix={() => { setError(''); setFormData((f) => ({ ...f, username: f.username.trim(), email: f.email.trim() })); }}
          />

          <div className="form-group">
            <label>Username *</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Email Address *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              readOnly={emailFromGoogle}
              style={emailFromGoogle ? { backgroundColor: '#f5f5f5' } : undefined}
            />
            {emailFromGoogle ? (
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.875rem' }}>
                Email matches your Google account and cannot be changed for this sign-up method.
              </small>
            ) : null}
          </div>

          {!googleCredential ? (
            <>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                />
                <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.875rem' }}>
                  Must be at least 8 characters
                </small>
              </div>

              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  minLength={8}
                />
              </div>
            </>
          ) : null}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Creating Account...' : googleCredential ? 'Create account with Google' : 'Create Account'}
          </button>
        </form>
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <Link to="/login" style={{ color: '#667eea', textDecoration: 'none', fontSize: '0.9rem' }}>
            Already have an account? Login
          </Link>
        </div>
      </div>
    </AuthPageShell>
  );
};

export default RegisterParent;




